"use client";

import type { Editor } from "@tiptap/core";
import posthog from "posthog-js";

import { useState } from "react";
import { LuAtSign } from "react-icons/lu";

import { Button } from "@/components/ui";

import { SuggestionItem } from "@/types/suggestions";

import { buildSuggestionMentionContent } from "./insert-suggestion";
import { TemplateVariableModal } from "./template-variable-modal";

interface InsertVariableButtonProps {
  /** The tiptap editor to insert into. Null while the editor is initializing. */
  editor: Editor | null;
  suggestions: SuggestionItem[];
  /** PostHog `surface` tag for the `suggestion_inserted` event. */
  posthogSurface?: string;
}

/**
 * Discoverable button → "Insert variable" modal. An *additional* entry point
 * that complements `@`-typing (which is unchanged). Inserts the selected
 * variable via the same shared mention-insertion path as the `@` popup.
 *
 * Renders nothing when there are no variables to insert (mirrors the email
 * composer's `hasMentions` gate; this also excludes async-only
 * `onSearchSuggestions` surfaces, which pass an empty static list).
 */
export function InsertVariableButton({
  editor,
  suggestions,
  posthogSurface,
}: InsertVariableButtonProps) {
  const [open, setOpen] = useState(false);

  if (suggestions.length === 0) {
    return null;
  }

  const handleSelect = (item: SuggestionItem) => {
    if (editor) {
      editor
        .chain()
        .focus()
        .insertContent(buildSuggestionMentionContent(item))
        .run();
      if (posthogSurface) {
        posthog.capture("suggestion_inserted", {
          suggestion_id: item.id,
          suggestion_type: item.type,
          surface: posthogSurface,
        });
      }
    }
    setOpen(false);
  };

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        icon={LuAtSign}
        tooltip="Insert variable"
        onClick={() => setOpen(true)}
        disabled={!editor}
      >
        Insert variable
      </Button>
      <TemplateVariableModal
        open={open}
        onOpenChange={setOpen}
        suggestions={suggestions}
        onSelect={handleSelect}
      />
    </>
  );
}
